import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Folder, Landmark, ScrollText, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { useLayer } from '../../lib/layers';
import { Icon } from '../ui/Icon';
import { useCan, type Need } from '../../lib/session';
import { visibleNav } from './nav';
import type { ScreenKey } from './nav';

/** Design: the first 10 matches. */
const MAX_ITEMS = 10;

export type RecordType = 'person' | 'project' | 'client' | 'contract' | 'entity';

/** GET /api/v1/search items (design `searchItems`). */
export interface SearchRecord {
  readonly type: RecordType;
  readonly id: string;
  readonly label: string;
  readonly sub: string;
}

export type PaletteTarget = { readonly type: 'screen'; readonly id: ScreenKey } | SearchRecord;

export interface PaletteItem {
  readonly key: string;
  readonly label: string;
  readonly sub: string;
  readonly icon: LucideIcon;
  readonly target: PaletteTarget;
}

const ICONS: Readonly<Record<RecordType, LucideIcon>> = {
  person: User,
  project: Folder,
  client: Building2,
  contract: ScrollText,
  entity: Landmark,
};

/** Case-insensitive substring match on "label sub" (design), capped at 10. */
export function filterPaletteItems(items: readonly PaletteItem[], query: string): PaletteItem[] {
  const q = query.trim().toLowerCase();
  const matches = q ? items.filter((item) => `${item.label} ${item.sub}`.toLowerCase().includes(q)) : items;
  return matches.slice(0, MAX_ITEMS);
}

function useSearchIndex() {
  return useQuery({
    queryKey: ['search-index'],
    queryFn: () => api.get<{ items: SearchRecord[] }>('/search'),
    select: (data) => data.items,
    staleTime: 30_000,
    retry: false,
  });
}

function buildItems(records: readonly SearchRecord[], can: (need: Need) => boolean): PaletteItem[] {
  const screens: PaletteItem[] = visibleNav(can).map((nav) => ({
    key: `screen:${nav.key}`,
    label: nav.label,
    sub: 'Экран',
    icon: ArrowRight,
    target: { type: 'screen', id: nav.key },
  }));
  const gaps: PaletteItem = {
    key: 'screen:gaps',
    label: 'Нужно дополнить',
    sub: 'Экран',
    icon: ArrowRight,
    target: { type: 'screen', id: 'gaps' },
  };
  const found = records.map((record) => ({
    key: `${record.type}:${record.id}`,
    label: record.label,
    sub: record.sub,
    icon: ICONS[record.type],
    target: record,
  }));
  return [...screens, gaps, ...found];
}

interface CommandPaletteProps {
  readonly onClose: () => void;
  readonly onPick: (target: PaletteTarget) => void;
}

/** ⌘K palette (design Main.dc.html:106-121): screens and records; ↑/↓ move, Enter opens. */
export function CommandPalette({ onClose, onPick }: CommandPaletteProps) {
  const [state, setState] = useState({ q: '', i: 0 });
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  useLayer({ kind: 'palette', panelRef, onClose, initialFocusRef: inputRef });
  const { data: records } = useSearchIndex();
  const can = useCan();
  const all = useMemo(() => buildItems(records ?? [], can), [records, can]);
  const items = filterPaletteItems(all, state.q);
  const active = Math.min(state.i, Math.max(0, items.length - 1));

  const pick = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    onPick(item.target);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setState((s) => ({ ...s, i: (active + step + items.length) % Math.max(1, items.length) }));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      pick(items[active]);
    }
  };

  return (
    <div onClick={onClose} className="fixed inset-0 z-[60] flex justify-center bg-scrim-search pt-24">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Поиск"
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[440px] w-[560px] flex-col overflow-hidden rounded-3 bg-bg-3 shadow-lg"
      >
        <input
          ref={inputRef}
          value={state.q}
          onChange={(event) => setState({ q: event.target.value, i: 0 })}
          onKeyDown={onKeyDown}
          placeholder="Люди, проекты, клиенты, договоры, экраны"
          aria-label="Поиск"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
          className="h-[52px] border-0 border-b border-border-1 bg-transparent px-[18px] text-[15px] text-fg-1 outline-none"
        />
        <div id={listId} role="listbox" aria-label="Результаты" className="overflow-y-auto p-1.5">
          {items.map((item, index) => (
            <button
              key={item.key}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={index === active}
              onClick={() => pick(item)}
              onMouseEnter={() => setState((s) => ({ ...s, i: index }))}
              className={cn(
                'flex h-[38px] w-full items-center gap-2.5 rounded-ctl border-0 px-3 text-left text-[13px] text-fg-1 hover:bg-bg-1',
                index === active ? 'bg-bg-1' : 'bg-transparent',
              )}
            >
              <Icon icon={item.icon} className="text-fg-2" />
              <span className="truncate font-medium">{item.label}</span>
              <span className="truncate text-fg-3">{item.sub}</span>
            </button>
          ))}
          {items.length === 0 && (
            <div className="px-3 py-4 text-fg-3">Ничего не найдено. Нажмите N, чтобы зафиксировать новое.</div>
          )}
        </div>
      </div>
    </div>
  );
}
